import React, { useEffect, useMemo, useState } from 'react';
import { 
  Box,
  Button,
  Flex,
  Text,
  useToast,
  SimpleGrid,
  Heading,
  Stack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  InputGroup,
  InputRightAddon,
  Spinner
} from '@chakra-ui/react';
import { cqAPI } from "../../lib/Auth";

function ProceduresManager() {
  const toast = useToast();

  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(false);

  // Add modal state
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newDuration, setNewDuration] = useState('');

  // Edit modal state
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editCode, setEditCode] = useState('');
  const [editDuration, setEditDuration] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const [query, setQuery] = useState('');

  const adaCodeRegex = /^D\d{4}$/i;
  const isValidCode = (code) => adaCodeRegex.test(String(code || '').trim());
  const isValidDuration = (mins) => {
    const n = Number(mins);
    return Number.isInteger(n) && n > 0 && n <= 600;
  };

  const fetchProcedures = async () => {
    try {
      setLoading(true);
      const res = await cqAPI.get('/settings/booking/procedures');
      const list = Array.isArray(res?.data) ? res.data : (res?.data?.procedures || []);
      setProcedures(list);
    } catch (error) {
      console.error('Error fetching procedures:', error);
      toast({ title: 'Failed to load procedures', description: error?.message, status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProcedures();
  }, []);

  const resetAddForm = () => {
    setNewCode('');
    setNewDuration('');
  };

  const openAdd = () => setIsAddOpen(true);
  const closeAdd = () => {
    setIsAddOpen(false);
    resetAddForm();
  };

  const openEdit = (proc) => {
    setSelectedId(proc?.id || proc?._id || null);
    setEditCode(proc?.code || '');
    setEditDuration(proc?.duration != null ? String(proc.duration) : '');
    setIsEditOpen(true);
  };
  const closeEdit = () => {
    setIsEditOpen(false);
    setSelectedId(null);
    setEditCode('');
    setEditDuration('');
  };

  const addProcedure = async () => {
    const code = newCode.trim().toUpperCase();
    const duration = Number(newDuration);

    if (!isValidCode(code)) {
      toast({ title: 'Invalid ADA code', description: 'Use format D#### (e.g., D0120).', status: 'error' });
      return;
    }
    if (!isValidDuration(duration)) {
      toast({ title: 'Invalid duration', description: 'Enter a whole number of minutes (1–600).', status: 'error' });
      return;
    }

    const exists = procedures.some(p => String(p.code).toUpperCase() === code);
    if (exists) {
      toast({ title: 'Code already exists', description: `${code} is already in your list.`, status: 'warning' });
      return;
    }

    try {
      const res = await cqAPI.post('/settings/booking/procedures', { code, duration });
      if (!res) return;
      if (res.type === 'ok' || res.status === 200 || res.status === 201) {
        toast({ title: 'Procedure added', status: 'success' });
        closeAdd();
        fetchProcedures();
      } else if (res.type === 'error') {
        toast({ title: 'Failed to add', description: res.msg, status: 'error' });
      }
    } catch (error) {
      toast({ title: 'Error adding procedure', description: error?.message, status: 'error' });
    }
  };

  const saveEdit = async () => {
    const code = editCode.trim().toUpperCase();
    const duration = Number(editDuration);

    if (!isValidCode(code)) {
      toast({ title: 'Invalid ADA code', description: 'Use format D#### (e.g., D0120).', status: 'error' });
      return;
    }
    if (!isValidDuration(duration)) {
      toast({ title: 'Invalid duration', description: 'Enter a whole number of minutes (1–600).', status: 'error' });
      return;
    }

    try {
      const payload = selectedId ? { id: selectedId, code, duration } : { code, duration };
      const res = await cqAPI.put('/settings/booking/procedures', payload);
      if (!res) return;
      if (res.type === 'ok' || res.status === 200) {
        toast({ title: 'Procedure updated', status: 'success' });
        closeEdit();
        fetchProcedures();
      } else if (res.type === 'error') {
        toast({ title: 'Failed to update', description: res.msg, status: 'error' });
      }
    } catch (error) {
      toast({ title: 'Error updating procedure', description: error?.message, status: 'error' });
    }
  };

  const filteredProcedures = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return procedures;
    return procedures.filter(p => {
      const code = String(p?.code || '').toLowerCase();
      const dur = String(p?.duration ?? '').toLowerCase();
      return code.includes(q) || dur.includes(q);
    });
  }, [procedures, query]);

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%" position="relative">
      <Flex justify="space-between" align="center" mb={4} gap={3} wrap="wrap">
        <Heading size="md">Procedure Codes & Durations</Heading>

        <Flex gap={2} align="center" minW={0}>
          <Input
            placeholder="Search by code or minutes..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            maxW="260px"
          />

          <Box flexShrink={0}>
            <Button
              colorScheme="blue"
              onClick={openAdd}
              flexShrink={0}
              flex="0 0 auto"
              minW="max-content"
              whiteSpace="nowrap"
              px={4}
            >
              Add Procedure
            </Button>
          </Box>
        </Flex>
      </Flex>

      <Box borderWidth="1px" borderRadius="lg" p={3} mb={3}>
        <Text fontSize="sm" color="gray.600">
          Tip: ADA codes look like <b>D0120</b>. Durations are in minutes and must be whole numbers.
        </Text>
      </Box>

      {loading ? (
        <Flex align="center" justify="center" minH="200px">
          <Spinner mr={3} /> <Text>Loading procedures…</Text>
        </Flex>
      ) : (
        <Box p={1} overflowY="auto" maxHeight="100vh" width="100%" position="relative">
          {filteredProcedures.length === 0 ? (
            <Box borderWidth="1px" borderRadius="lg" p={6} textAlign="center">
              <Text color="gray.600">No procedures found.</Text>
            </Box>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing={3}>
              {filteredProcedures.map((proc) => {
                const { id, _id, code, duration } = proc;
                const key = id || _id || code;
                return (
                  <Box key={key} borderWidth="1px" borderRadius="lg" mb={1} width="100%" className="actionableItem">
                    <Flex direction="column" p={4} justify="space-between" height="100%">
                      <Flex justify="space-between" align="center" textAlign="left" gap={3}>
                        <Box>
                          <Text fontSize="sm"> Code: <b>{String(code).toUpperCase()}</b></Text>
                          <Text fontSize="sm"> Duration: <b>{duration} min</b></Text>
                        </Box>
                        <Button onClick={() => openEdit(proc)}>
                          Edit
                        </Button>
                      </Flex>
                    </Flex>
                  </Box>
                );
              })}
            </SimpleGrid>
          )}
        </Box>
      )}

      {/* Add Modal */}
      <Modal isOpen={isAddOpen} onClose={closeAdd}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add a procedure</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={3}>
              <Input 
                placeholder="ADA code (e.g., D0120)"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
                textTransform="uppercase"
              />
              <InputGroup>
                <Input
                  placeholder="Duration in minutes"
                  type="number"
                  min={1}
                  max={600}
                  value={newDuration}
                  onChange={(e) => setNewDuration(e.target.value)}
                />
                <InputRightAddon>min</InputRightAddon>
              </InputGroup>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={addProcedure}>
              Save
            </Button>
            <Button variant="ghost" onClick={closeAdd}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={closeEdit}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit procedure</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={3}>
              <Input 
                placeholder="ADA code (e.g., D0120)"
                value={editCode}
                onChange={(e) => setEditCode(e.target.value)}
                textTransform="uppercase"
              />
              <InputGroup>
                <Input
                  placeholder="Duration in minutes"
                  type="number"
                  min={1}
                  max={600}
                  value={editDuration}
                  onChange={(e) => setEditDuration(e.target.value)}
                />
                <InputRightAddon>min</InputRightAddon>
              </InputGroup>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={saveEdit}>
              Save changes
            </Button>
            <Button variant="ghost" onClick={closeEdit}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default ProceduresManager;
